import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { EventsType, useIdleTimer } from 'react-idle-timer';
import { authApi } from 'src/api';
import { isAxiosError } from 'src/api/instance';
import { fetchCurrentUser } from 'src/features/auth';
import { actions as authActions, selectors as authSelectors } from 'src/features/auth/authSlice';
import { RootState, useAppDispatch } from 'src/store';
import Modal, { ModalVariant } from '../components/organisms/Modal';

type RefreshResult = boolean | 'unauthorized';

const isUnauthorized = (error: unknown) => isAxiosError(error) && error.response?.status === 401;

const hasFetchUnauthorizedSelector = (state: RootState) =>
  Object.keys(state.folders.fetchFailedIds).length > 0 ||
  Object.keys(state.entries.fetchFailedIds).length > 0;

const DEBOUNCE_TIME = 5 * 1000; // 5 seconds in mills
const PROMPT_BEFORE_IDLE_MS = 30000;

// Proactive refresh: keeps active users' tokens fresh well before expiry
const PROACTIVE_REFRESH_MAX_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes max between refreshes
const MIN_REFRESH_INTERVAL_MS = 30 * 1000; // minimum 30 seconds (safety floor)

const OBSERVED_EVENT: EventsType[] = [
  'click',
  'mousemove',
  'keydown',
  'wheel',
  'DOMMouseScroll',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'MSPointerDown',
  'MSPointerMove',
  'visibilitychange'
];

type SessionState = 'Active' | 'Idle' | 'Prompted' | 'Expired';

export const LogOutSessionModal = () => {
  const authenticatedUser = useSelector(authSelectors.user);
  const hasFetchUnauthorized = useSelector(hasFetchUnauthorizedSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const timeUntilTokenExpires = authenticatedUser?.exp
    ? Math.max(authenticatedUser.exp - PROMPT_BEFORE_IDLE_MS, 0)
    : PROMPT_BEFORE_IDLE_MS;

  // Compute proactive refresh interval: 50% of token lifetime, capped at 5 minutes
  const refreshInterval = authenticatedUser?.exp
    ? Math.max(
        Math.min(authenticatedUser.exp * 0.5, PROACTIVE_REFRESH_MAX_INTERVAL_MS),
        MIN_REFRESH_INTERVAL_MS
      )
    : PROACTIVE_REFRESH_MAX_INTERVAL_MS;

  const [state, setState] = useState<SessionState>('Active');
  const [remaining, setRemaining] = useState<number>(0);

  // Prevent concurrent refresh attempts
  const isRefreshingRef = useRef<boolean>(false);
  const lastFetchUnauthorizedCheckRef = useRef<number>(0);
  // Mirror state in a ref so the visibilitychange listener has a stable closure
  const stateRef = useRef<SessionState>('Active');
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Attempt to refresh the session silently. Returns true on success.
  const attemptRefresh = useCallback(async (): Promise<RefreshResult> => {
    if (isRefreshingRef.current) return false;
    isRefreshingRef.current = true;
    try {
      await authApi.refreshSession();
      dispatch(fetchCurrentUser({ isRefreshSessionCaller: true }));
      return true;
    } catch (error) {
      console.error('Session refresh failed:', error);
      return isUnauthorized(error) ? 'unauthorized' : false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [dispatch]);

  const onIdle = () => {
    // Do NOT auto-logout. Just mark the state as idle.
    // When the user returns (via visibilitychange or interaction), we'll attempt
    // a silent refresh. Only if the refresh token has expired do we show the login prompt.
    setState('Idle');
  };

  // Called when the user becomes active again after being idle (e.g., clicking "I'm still here" on the modal)
  const onActive = useCallback(async () => {
    if (!authenticatedUser) return;
    const success = await attemptRefresh();
    if (success === true) {
      setState('Active');
    } else {
      // Retry once after a short delay
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const retrySuccess = await attemptRefresh();
      if (retrySuccess === true) {
        setState('Active');
      } else {
        // Refresh token has expired — force re-login
        setState('Expired');
      }
    }
  }, [authenticatedUser, attemptRefresh]);

  const onPrompt = () => {
    setState('Prompted');
  };

  const { getRemainingTime, getTotalIdleTime, activate, reset } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout: timeUntilTokenExpires,
    promptTimeout: PROMPT_BEFORE_IDLE_MS,
    debounce: DEBOUNCE_TIME,
    events: OBSERVED_EVENT
  });

  // A refresh 401 can come from a race with another tab, which shares the session cookie,
  // so ask /auth/user twice before telling the analyst the session is gone.
  const isSessionGone = useCallback(async (): Promise<boolean> => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 1000));
      try {
        await authApi.currentAuthenticatedUser();
        dispatch(fetchCurrentUser({ isRefreshSessionCaller: true }));
        return false;
      } catch (error) {
        if (!isUnauthorized(error)) return false;
      }
    }
    return true;
  }, [dispatch]);

  const checkSession = useCallback(async () => {
    const result = await attemptRefresh();
    if (result === true) {
      reset();
    } else if (result === 'unauthorized' && (await isSessionGone())) {
      setState('Expired');
    }
  }, [attemptRefresh, reset, isSessionGone]);

  // Proactive token refresh: periodically refresh the session while the user is active.
  // Uses getRemainingTime() from the idle timer — if > 0, the user has interacted recently.
  useEffect(() => {
    if (!authenticatedUser) return;

    const interval = setInterval(async () => {
      if (stateRef.current !== 'Expired' && getRemainingTime() > 0) {
        await checkSession();
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [authenticatedUser, refreshInterval, checkSession, getRemainingTime]);

  // A folder or entry fetch got a 401: check the session now instead of waiting for the interval.
  useEffect(() => {
    if (!authenticatedUser || !hasFetchUnauthorized || stateRef.current === 'Expired') return;
    if (Date.now() - lastFetchUnauthorizedCheckRef.current < MIN_REFRESH_INTERVAL_MS) return;
    lastFetchUnauthorizedCheckRef.current = Date.now();
    checkSession();
  }, [authenticatedUser, hasFetchUnauthorized, checkSession]);

  // Silent refresh on tab return: when the user switches back to this tab,
  // attempt to refresh the session. This handles the "left for 1 hour, came back" case.
  // Uses stateRef so the listener registers once (no re-registration on state changes).
  useEffect(() => {
    if (!authenticatedUser) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      // Only attempt refresh if user has been idle or token may have expired
      if (stateRef.current === 'Idle' || stateRef.current === 'Prompted') {
        const success = await attemptRefresh();
        if (success === true) {
          reset();
          setState('Active');
        } else {
          setState('Expired');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [authenticatedUser, attemptRefresh, reset]);

  // Update the remaining time every 500ms for the countdown in the modal
  useEffect(() => {
    if (state !== 'Prompted') return;
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [state, getRemainingTime]);

  if (authenticatedUser) {
    return (
      <>
        <Modal
          title="Inactive session"
          text={`You will be logged out in ${remaining} seconds`}
          labelButtonCancel="Log out"
          labelButtonConfirm={`I'm still here`}
          showModal={state === 'Prompted'}
          variant={ModalVariant.none}
          onCancel={() => {
            navigate('/logout');
          }}
          onConfirm={() => {
            activate();
          }}
        />
        <Modal
          isBackClosable={false}
          showCancelButton={false}
          blurBackground={true}
          title="Session expired"
          text={`Your session has expired after ${Math.floor(
            getTotalIdleTime() / 60000
          )} minutes of inactivity. Please log in again.`}
          labelButtonConfirm={`Log in`}
          showModal={state === 'Expired'}
          variant={ModalVariant.none}
          onConfirm={() => {
            setState('Active');
            dispatch(authActions.logout());
          }}
        />
      </>
    );
  }
  return null;
};

export default LogOutSessionModal;
